import {ILookup} from 'models/common/ILookup';
import {ResourceTypeNumEnum} from 'admin/resources/enums';
import {
  emptyResourceMetadataLookupNames,
  IResourceMetadataLookupNames,
  toNumberIds,
  toNumberLookup,
  toNumberLookups,
  toOptionalBoolean,
  toOptionalNumber,
  toStringLookups,
} from 'admin/resources/utils';
import {IEditFileMetadataFormBaseValues} from '../components/FilesEditResourceForm/config';

const toNumberLookupIdOnly = (id?: number | string | null): ILookup<number> | undefined => {
  const parsed = toOptionalNumber(id);
  return parsed === undefined ? undefined : {id: parsed, name: ''};
};

const mapImageMetadata = (formValues: IEditFileMetadataFormBaseValues, lookupNames: IResourceMetadataLookupNames) => ({
  stockImageLinkNumber: formValues.stockImageLinkNumber ?? '',
  imageSources: toNumberLookups(formValues.imageSources, lookupNames.imageSources),
  ethnicities: toNumberLookups(formValues.ethnicities, lookupNames.ethnicities),
  genders: toNumberLookups(formValues.genders, lookupNames.genders),
  publishedOutputTool: toNumberLookup(formValues.publishedOutputTool, lookupNames.publishedOutputTools),
  alternateText: formValues.alternateText ?? '',
  linkToStockImageSource: formValues.linkToStockImageSource ?? '',
});

export const mapResourceMetadata = (
  formValues: IEditFileMetadataFormBaseValues,
  lookupNames: IResourceMetadataLookupNames = emptyResourceMetadataLookupNames()
) => {
  const {
    keyTerms,
    learningObjectives,
    expirationDate,
    languages,
    programs,
    pillars,
    gradeLevels,
    isAccessibleToStudents,
    competencies,
    knowledgeSkillsAndAbilities,
    time,
  } = formValues;

  const associatedResources = toStringLookups(formValues.associatedResources, lookupNames.associatedResources);
  const explicitResourceIds = toStringLookups(
    formValues.resourceIds?.length ? formValues.resourceIds : [],
    lookupNames.associatedResources
  ).map((lookup) => lookup.id);
  const resourceIds = explicitResourceIds.length ? explicitResourceIds : associatedResources.map((lookup) => lookup.id);
  const numberOfResources = toOptionalNumber(formValues.numberOfResources);
  const isImageFileType = formValues.fileType === ResourceTypeNumEnum.Image;

  return {
    isAccessibleToStudents: toOptionalBoolean(isAccessibleToStudents),
    keyTerms: keyTerms ?? '',
    learningObjectives: learningObjectives ?? '',
    accessExpirationDate: expirationDate ?? null,
    languages: toNumberIds(languages),
    competencies: toNumberIds(competencies),
    pillars: toNumberIds(pillars),
    programs: toNumberIds(programs),
    gradeLevels: toNumberIds(gradeLevels),
    kSAs: toNumberIds(knowledgeSkillsAndAbilities),
    lengthInMinutes: time === 0 || time === undefined ? null : time,

    permissionNeeded: toOptionalBoolean(formValues.permissionNeeded),
    ...(isImageFileType ? {imageMetadata: mapImageMetadata(formValues, lookupNames)} : {}),
    slideNumber: toOptionalNumber(formValues.slideNumber),
    sessionThemeNumber: toOptionalNumber(formValues.sessionThemeNumber),
    associatedResources,
    fileName: formValues.fileName ?? formValues.file?.name ?? '',
    link: formValues.link ?? '',
    mediaRights: {
      urlSource: formValues.urlSource ?? '',
      usageType: toNumberLookupIdOnly(formValues.usageType),
      reuseFlag: toOptionalBoolean(formValues.reuseFlag),
      notes: formValues.notes ?? '',
      permissionAgreementUrl: formValues.permissionAgreementUrl ?? '',
    },
    numberOfResources: numberOfResources ?? associatedResources.length,
    resourceIds,
    characterTraits: toNumberLookups(formValues.characterTraits, lookupNames.characterTraits),
    behavioralIndicators: toNumberLookups(formValues.behavioralIndicators, lookupNames.behavioralIndicators),
    videoTranscriptionLink: formValues.videoTranscriptionLink ?? '',
    credentialing: toOptionalBoolean(formValues.credentialing),
    permissionInPerpetuity: formValues.permissionInPerpetuity ?? false,
    accessibilityStatus: toNumberLookup(formValues.accessibilityStatus, lookupNames.accessibilityStatuses) ?? null,
    accessibilityReports: formValues.accessibilityReports ?? '',
  };
};
